import { useMutation } from "@tanstack/react-query";
import { handbackTutorQueryKey, postFlowTutorClaim } from "@/api/admin.ts";
import { HttpError } from "@/api/shared.ts";
import { toastAndError } from "@/helpers/toast.ts";
import { usePendingToast } from "@/helpers/usePendingToast.ts";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";

/**
 * The claim-session flow shared by ChatHeader and the pre-claim review panel:
 * store teardown, the claim call (isLLMAugmented always true), the hand-back
 * probe refresh (ownership changes flip its answer), and the indefinite
 * "Claiming session..." toast while pending.
 *
 * A 400 means the session is already claimed — ChatHeader passes
 * onAlreadyClaimed to open its override modal, the review panel to inform the
 * tutor they lost the race. Without it the 400 falls through to the error toast.
 */
export const useClaimSession = (
  flowGeneratorSessionId: number,
  onAlreadyClaimed?: () => void,
  // Fires on a successful claim — the review panel uses it to tell the chat
  // view "this is now a REAL claim, not a review assignment"
  onClaimed?: () => void,
) => {
  const claimSession = useMutation({
    mutationFn: (override: boolean) => {
      const { RemoveSession } = useAssignedChatStore.getState();
      RemoveSession(flowGeneratorSessionId);

      return postFlowTutorClaim(flowGeneratorSessionId, true, override);
    },
    onSuccess: () => {
      // Ownership changed — re-ask the dedicated endpoint whether hand-back
      // now applies
      window.QueryClient.invalidateQueries({
        queryKey: handbackTutorQueryKey(flowGeneratorSessionId),
      });
      onClaimed?.();
    },
  });

  // Note: may need to add session manually, but for now auto
  // catch in TutorWrapper.tsx will pick up claimed sessions
  const attemptClaim = (override: boolean) => {
    if (claimSession.isPending) return;

    claimSession.mutateAsync(override).catch((e: Error) => {
      if (e instanceof HttpError && e.statusCode === 400 && onAlreadyClaimed) {
        onAlreadyClaimed();
        return;
      }

      toastAndError("Could not claim session", e);
    });
  };

  usePendingToast(claimSession.isPending, "Claiming session...");

  return { attemptClaim, isPending: claimSession.isPending };
};
